import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import CommonNestedTable from '@/components/common/CommonNestedTable';
import { TableColumn } from '@/components/common/CommonTable';
import Pagination from '@/components/common/Pagination';
import StatusDropdown from '@/components/common/StatusComponent';
import Card from '@/components/UI/Card';
import { useDebounce } from '@/hooks/useDebounce';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import {
  ICategoryData,
  ICategoryResponse,
} from '@/interfaces/categoryResponse.interface';
import { PageInfo } from '@/interfaces/common.interface';
import { GetFilterUrl } from '@/utility/helper';
import React, { useEffect, useState } from 'react';
import FormField from '../UI/FormField';
import ImagePreview from '../common/media/ImagePreview';

interface CategoryStates {
  isLoading: boolean;
  categoryStatus: string;
  categories: ICategoryData[];
  pageInfo: PageInfo;
  selectedCategories: Set<string | number>;
}

interface CategoryTableProp {
  onSelectCtegory?: (selected: ICategoryData[]) => void;
  selectionMode?: 'single' | 'multiple';
  initialSelectedId?: string;
}

const CategoryTable: React.FC<CategoryTableProp> = ({
  onSelectCtegory,
  selectionMode = 'multiple',
  initialSelectedId,
}) => {
  const [singleSelectedId, setSingleSelectedId] = useState<
    string | number | null
  >(null);
  const [searchValue, setSearchValue] = useState('');
  const debounceValue = useDebounce(searchValue);
  const [states, setStates] = useState<CategoryStates>({
    isLoading: false,
    categoryStatus: 'active',
    categories: [],
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit: 10,
      page: 1,
    },
    selectedCategories: new Set(),
  });

  const handleChangeState = (newState: Partial<typeof states>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const fetchCategories = async () => {
    try {
      const filterQuery = {
        page: states.pageInfo.page,
        limit: states.pageInfo.limit,
        status: states.categoryStatus,
        search: debounceValue,
      };
      const query = GetFilterUrl(filterQuery);
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.categories}?${query ? `${query}` : ''}`;
      const res = await callApi<ICategoryResponse>(PATH, { method: 'GET' });
      handleChangeState({
        categories: res.data,
        pageInfo: {
          limit: +res.pageInfo.limit,
          page: +res.pageInfo.page,
          total: +res.pageInfo.total,
          totalPages: +res.pageInfo.totalPages,
        },
        isLoading: false,
      });
    } catch (error) {
      handleChangeState({ isLoading: false });
      console.log('error is', error);
    }
  };

  const handleSelectCategory = (category: ICategoryData) => {
    if (selectionMode === 'single') {
      setSingleSelectedId(category.id);

      onSelectCtegory?.([category]);
    } else {
      const isSelected = selectedItems.has(category.id);
      handleSelectItem(category, !isSelected);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [
    states.pageInfo.page,
    states.pageInfo.limit,
    states.categoryStatus,
    debounceValue,
  ]);

  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(states.categories);

  useEffect(() => {
    if (selectionMode === 'multiple') {
      const selectedData = states.categories.filter((c) =>
        selectedItems.has(c.id),
      );
      onSelectCtegory?.(selectedData);
    }
    if (
      initialSelectedId &&
      selectionMode === 'single' &&
      states.categories.length > 0
    ) {
      const selectedCategory = states.categories.find(
        (c) => c.id === initialSelectedId,
      );
      if (selectedCategory) {
        setSingleSelectedId(selectedCategory.id);
        onSelectCtegory?.([selectedCategory]);
      }
    }
  }, [selectedItems, states.categories, selectionMode, initialSelectedId]);

  const columns: TableColumn<ICategoryData>[] = [
    {
      key: 'name',
      header: 'Category',
      render: (item) => {
        return (
          <ImagePreview
            src={`${API_URL_PRODUCT_SERVICE}/${item?.media[0]?.url}`}
            alt=""
            text={item.name}
          />
        );
      },
    },
    {
      key: 'productCount',
      header: 'Products',
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <StatusDropdown
          value={item.status}
          options={[]}
          canEditVal={'category'}
          onChange={() => {}}
        />
      ),
    },
  ];

  const childColumns: TableColumn<ICategoryData>[] = [
    {
      key: 'name',
      header: 'Sub Category',
      className: 'p-3',
    },
    {
      key: 'description',
      header: 'Description',
      className: 'p-3',
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <StatusDropdown value={item.status} options={[]} onChange={() => {}} />
      ),
    },
  ];

  const handleChangePage = (page: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        page,
      },
    });
  };

  const handleSizeChange = (limit: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        limit,
      },
    });
  };

  return (
    <>
      <Card>
        <div className="mb-2">
          <FormField
            placeholder="Search for the categories.."
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </div>
        <CommonNestedTable
          columns={columns}
          data={states.categories}
          showCheckbox={true}
          selectedItems={
            selectionMode === 'single'
              ? new Set(singleSelectedId ? [singleSelectedId] : [])
              : selectedItems
          }
          onSelectAll={
            selectionMode === 'multiple' ? handleSelectAll : undefined
          }
          onSelectItem={handleSelectCategory}
          getItemId={(item) => item.id}
          emptyMessage="No Categories Found"
          loading={states.isLoading}
          getChildren={(item) => item.subcategories}
          childColumns={childColumns}
        />
        <Pagination
          paginationData={states.pageInfo}
          onPageChange={(page) => handleChangePage(page)}
          isLoading={states.isLoading}
          onPageSizeChange={(size) => handleSizeChange(size)}
        />
      </Card>
    </>
  );
};

export default CategoryTable;
